import type { PlanId } from './types';

/**
 * Planes y limites de uso.
 *
 * El asistente se paga por token, asi que es el unico coste variable del
 * producto: si no se acota, un tenant curioso puede costar mas de lo que paga.
 * Aqui vive la unica fuente de verdad de esos limites; el API los aplica y el
 * frontend los muestra.
 */

export interface PlanDefinition {
  id: PlanId;
  name: string;
  /** Precio mensual sin IVA */
  pricePerMonth: number;
  /** Mensajes al asistente incluidos al mes. null = sin limite */
  assistantMessagesPerMonth: number | null;
  /**
   * Escaneos de factura incluidos al mes. 0 = el plan no lo trae y hace falta
   * contratar el modulo. Ver ADDONS.
   */
  receiptScansPerMonth: number;
  maxLocations: number;
  maxRecipes: number | null;
  maxUsers: number;
  features: string[];
}

/** Días de prueba gratuita (los Founder no tienen: pagan desde el primer día). */
export const TRIAL_DAYS = 14;

export const PLANS: Record<PlanId, PlanDefinition> = {
  trial: {
    id: 'trial',
    name: 'Prueba',
    pricePerMonth: 0,
    assistantMessagesPerMonth: 20,
    // La prueba trae unos pocos escaneos a proposito: es la forma de que vean
    // lo que hace antes de que nadie les pida dinero por ello.
    receiptScansPerMonth: 10,
    maxLocations: 1,
    maxRecipes: 25,
    maxUsers: 1,
    features: ['Costeo de recetas', 'Cuenta de resultados', 'Asistente limitado'],
  },
  basic: {
    id: 'basic',
    name: 'Básico',
    pricePerMonth: 49,
    assistantMessagesPerMonth: 150,
    receiptScansPerMonth: 0,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 1,
    features: [
      '1 usuario (amplía con usuarios adicionales)',
      '1 local incluido (amplía por 9 €/mes cada uno)',
      'Recetas y subrecetas sin límite',
      'Precios con comisiones de delivery',
      'Cuenta de resultados y tesorería',
      'Cuentas por pagar',
      '150 preguntas al asistente al mes',
    ],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    pricePerMonth: 79,
    assistantMessagesPerMonth: 300,
    receiptScansPerMonth: 300,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 5,
    features: [
      'Todo lo del plan Básico',
      '5 usuarios incluidos',
      '1 local incluido (amplía por 9 €/mes cada uno)',
      'Objetivos y periodificación de impuestos',
      'Menu engineering y alertas de recosteo',
      '300 preguntas al asistente al mes',
      'Escáner de facturas incluido (300 al mes)',
    ],
  },
  /**
   * Founder (cohorte fundadora, feedback 3 de Alex).
   *
   * 50 plazas, 39 €/mes con el precio BLOQUEADO mientras la suscripción siga
   * activa. Es Pro completo pero con 1 ubicación y 300 consultas/escaneos, y
   * SIN prueba gratuita (se paga desde el día 1). Si cancelan y vuelven,
   * entran al precio vigente — el precio Founder no se recupera. La cohorte de
   * 50 se controla desde Stripe/negocio, no en el código.
   */
  founder: {
    id: 'founder',
    name: 'Founder',
    pricePerMonth: 39,
    assistantMessagesPerMonth: 300,
    receiptScansPerMonth: 300,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 5,
    features: [
      'Pro completo con 1 local incluido',
      'Precio de fundador bloqueado mientras sigas suscrito',
      '5 usuarios incluidos',
      '300 escaneos de factura al mes',
      '300 preguntas al asistente al mes',
      'Acceso anticipado a nuevas funciones',
      'Onboarding directo y canal privado de feedback',
    ],
  },
};

/** ¿El plan incluye el escáner de facturas de serie? (Pro y Founder). */
export function scannerIncluido(plan: PlanId): boolean {
  return (PLANS[plan]?.receiptScansPerMonth ?? 0) > 0 && (plan === 'pro' || plan === 'founder');
}

/**
 * Lo que un tenant aporta al MRR: plan + escaner suelto + asientos y centros
 * extra. Cuenta tambien a los `past_due` —siguen suscritos y Stripe los sigue
 * intentando cobrar—; solo salen del MRR al cancelar.
 *
 * Es una estimacion desde nuestros datos (los webhooks de Stripe): la cifra
 * exacta, con cupones y prorrateos, la da siempre Stripe.
 */
export function mrrDeTenant(tenant: {
  plan: PlanId;
  subscriptionStatus: string;
  addons?: readonly AddonId[];
  extraSeats?: number;
  extraLocations?: number;
}): number {
  if (tenant.subscriptionStatus !== 'active' && tenant.subscriptionStatus !== 'past_due') {
    return 0;
  }
  const plan = PLANS[tenant.plan]?.pricePerMonth ?? 0;
  const escaner = (tenant.addons ?? []).includes('escaner') ? ADDONS.escaner.pricePerMonth : 0;
  const asientos = Math.max(0, tenant.extraSeats ?? 0) * EXTRA_SEAT_PRICE_PER_MONTH;
  const centros = Math.max(0, tenant.extraLocations ?? 0) * EXTRA_LOCATION_PRICE_PER_MONTH;
  return plan + escaner + asientos + centros;
}

/* ------------------------------------------------------------------ */
/* Coste del asistente                                                 */
/* ------------------------------------------------------------------ */

/**
 * Coste por millon de tokens del modelo del asistente, en euros.
 *
 * Es una estimacion para poder vigilar el margen sin salir de la app: la cifra
 * buena es siempre la factura de AWS. Se sobrescribe por variable de entorno
 * cuando cambien los precios de Bedrock, para no tener que desplegar codigo.
 */
export interface ModelPricing {
  inputPerMillion: number;
  outputPerMillion: number;
}

export const DEFAULT_MODEL_PRICING: ModelPricing = {
  inputPerMillion: 3,
  outputPerMillion: 15,
};

export interface AssistantUsage {
  tenantId: string;
  /** YYYY-MM */
  month: string;
  messages: number;
  /** Facturas escaneadas este mes. Cuota aparte de la del asistente. */
  scans: number;
  inputTokens: number;
  outputTokens: number;
  /** Coste estimado acumulado del mes, en euros */
  estimatedCost: number;
  updatedAt: string;
}

export interface UsageStatus {
  month: string;
  plan: PlanId;
  planName: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  /** Primer dia del mes siguiente, cuando se reinicia el contador */
  resetsOn: string;
  estimatedCost: number;
  /** true si ya no puede preguntar mas este mes */
  exhausted: boolean;
  /** true a partir del 80 % del limite, para avisar antes de cortar */
  nearLimit: boolean;
}

export function estimateCost(
  inputTokens: number,
  outputTokens: number,
  pricing: ModelPricing = DEFAULT_MODEL_PRICING,
): number {
  const cost =
    (inputTokens / 1_000_000) * pricing.inputPerMillion +
    (outputTokens / 1_000_000) * pricing.outputPerMillion;
  return Math.round(cost * 10_000) / 10_000;
}

export function nextMonthStart(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

export function usageStatus(
  plan: PlanId,
  usage: Pick<AssistantUsage, 'month' | 'messages' | 'estimatedCost'>,
): UsageStatus {
  const definition = PLANS[plan] ?? PLANS.trial;
  const limit = definition.assistantMessagesPerMonth;
  const used = usage.messages;
  const remaining = limit == null ? null : Math.max(0, limit - used);

  return {
    month: usage.month,
    plan: definition.id,
    planName: definition.name,
    used,
    limit,
    remaining,
    resetsOn: nextMonthStart(usage.month),
    estimatedCost: usage.estimatedCost,
    exhausted: limit != null && used >= limit,
    nearLimit: limit != null && used >= limit * 0.8,
  };
}

/** Mensaje que ve el duenyo cuando agota su cuota. Sin jerga ni culpa. */
export function quotaMessage(status: UsageStatus): string {
  const fecha = new Date(`${status.resetsOn}T00:00:00Z`).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  return (
    `Has usado las ${status.limit} preguntas al asistente que incluye tu plan ${status.planName} este mes. ` +
    `El contador se reinicia el ${fecha}. Si te has quedado corto, puedes subir de plan desde Ajustes.`
  );
}

/* ------------------------------------------------------------------ */
/* Modulos de pago                                                     */
/* ------------------------------------------------------------------ */

export type AddonId = 'escaner';

export interface AddonDefinition {
  id: AddonId;
  name: string;
  /** Precio mensual sin IVA, en euros. */
  pricePerMonth: number;
  /** Escaneos incluidos al mes. */
  scansPerMonth: number;
  description: string;
}

/**
 * Escaner de facturas.
 *
 * Se cobra aparte en Basico y va incluido en Pro. La razon NO es el coste:
 * leer una factura cuesta alrededor de un centimo, asi que un cliente que
 * suba cien al mes cuesta un euro sobre una cuota de 49. Es una decision de
 * valor — dejar de picar facturas a mano es de las pocas cosas por las que un
 * restaurante paga sin pensarlo.
 *
 * Va como cuota mensual con escaneos incluidos, no por escaneo. Cobrar por
 * escaneo convierte cada foto en una decision ("¿merece la pena escanear un
 * ticket de 12 euros?") y el habito es justo lo que hace que el modulo siga
 * contratado el mes siguiente.
 */
export const ADDONS: Record<AddonId, AddonDefinition> = {
  escaner: {
    id: 'escaner',
    name: 'Escáner de facturas',
    pricePerMonth: 9,
    scansPerMonth: 150,
    description:
      'Sube una foto o PDF de una factura y Alexia extraerá los datos para proponerte el gasto: proveedor, fecha, importe sin IVA, IVA y total. Tú solo tienes que revisarlo y confirmar.',
  },
};

/* ------------------------------------------------------------------ */
/* Asientos                                                            */
/* ------------------------------------------------------------------ */

/**
 * Asiento extra: 5 €/mes por usuario por encima de los que incluye el plan
 * (Basico 1 · Pro 5). No es un AddonId porque lleva cantidad — se contratan
 * N asientos, no un modulo que esta o no esta. En Stripe es una linea propia
 * cuya `quantity` acaba en `Tenant.extraSeats` via webhook.
 *
 * Decision de precio (Alex, 21 ago 2026): Basico se queda en 1 usuario a
 * proposito, pero el asiento extra es barato para que compartir contraseña
 * no compense — compartirla mata la trazabilidad de quien metio cada dato.
 */
export const EXTRA_SEAT_PRICE_PER_MONTH = 5;

/** Centro extra: 9 €/mes por cada centro por encima del incluido. */
export const EXTRA_LOCATION_PRICE_PER_MONTH = 9;

/** Asientos totales de un tenant: los del plan mas los contratados. */
export function seatLimitFor(plan: PlanId, extraSeats = 0): number {
  const delPlan = PLANS[plan]?.maxUsers ?? 1;
  return delPlan + Math.max(0, Math.floor(extraSeats));
}

/** Centros totales de un tenant: uno incluido en cualquier plan más los contratados. */
export function locationLimitFor(plan: PlanId, extraLocations = 0): number {
  const delPlan = PLANS[plan]?.maxLocations ?? 1;
  return delPlan + Math.max(0, Math.floor(extraLocations));
}

/** Escaneos al mes de un tenant, sumando lo del plan y lo del modulo. */
export function scanLimitFor(plan: PlanId, addons: readonly AddonId[] = []): number {
  const delPlan = PLANS[plan]?.receiptScansPerMonth ?? 0;
  const delModulo = addons.includes('escaner') ? ADDONS.escaner.scansPerMonth : 0;
  return delPlan + delModulo;
}

/** ¿Tiene derecho a escanear? (aunque haya agotado la cuota del mes) */
export const puedeEscanear = (plan: PlanId, addons: readonly AddonId[] = []): boolean =>
  scanLimitFor(plan, addons) > 0;

export interface ScanQuota {
  used: number;
  limit: number;
  remaining: number;
  exhausted: boolean;
  nearLimit: boolean;
  /** true = ni siquiera tiene el modulo contratado. */
  sinModulo: boolean;
  resetsOn: string;
}

export function scanQuota(
  plan: PlanId,
  addons: readonly AddonId[],
  used: number,
  month: string,
): ScanQuota {
  const limit = scanLimitFor(plan, addons);
  return {
    used,
    limit,
    remaining: Math.max(0, limit - used),
    exhausted: limit > 0 && used >= limit,
    nearLimit: limit > 0 && used >= limit * 0.8,
    sinModulo: limit === 0,
    resetsOn: nextMonthStart(month),
  };
}

/** Lo que se le dice a quien no puede escanear. Sin jerga y con salida. */
export function scanQuotaMessage(quota: ScanQuota, planName: string): string {
  if (quota.sinModulo) {
    return (
      `El plan ${planName} no incluye el escáner de facturas. ` +
      `Puedes contratarlo desde Ajustes por ${ADDONS.escaner.pricePerMonth} € al mes ` +
      `(${ADDONS.escaner.scansPerMonth} escaneos), o subir al plan Pro, que lo incluye ` +
      `(${PLANS.pro.receiptScansPerMonth} escaneos al mes).`
    );
  }
  const fecha = new Date(`${quota.resetsOn}T00:00:00Z`).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  return (
    `Has usado los ${quota.limit} escaneos de este mes. El contador se reinicia el ${fecha}. ` +
    'Mientras tanto puedes seguir registrando gastos manualmente.'
  );
}
